import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { 
  BarChart3, Users, MapPin, LogOut, FileText, Brain, Briefcase, Factory, 
  ChevronRight, ChevronDown, Menu, X, TrendingUp, AlertTriangle, BookOpen, Rocket, Bot, 
  Settings, Activity, Lightbulb, Zap, CheckSquare, Clock, ClipboardList, ClipboardCheck, Network, Lock, ShieldAlert, ShieldCheck, Plus, Sparkles
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useToast } from "@/hooks/use-toast";
import { toast } from "sonner";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import AdminUsers from "./AdminUsers";
import AdminSites from "./AdminSites";
import AdminBehavioral from "./AdminBehavioral";
import AdminAnalytics from "./AdminAnalytics";
import AdminPositions from "./AdminPositions";
import AdminDepartments from "./AdminDepartments";
import AdminReports from "./AdminReports";
import AdminDocuments from "./AdminDocuments";
import AdminIntervention from "./AdminInterventions";
import AdminSettings from "./AdminSettings";
import AdminFrictionMapping from "./AdminFrictionMapping";
import SupervisorAction from "./SupervisorAction";
import ResultsDashboard from "./ResultsDashboard";
import AdminCheckins from "./AdminCheckins";
import AdminExperimentResults from "./AdminExperimentResults";
import SignalHub from "./SignalHub";
import WorkerDiagnostics from "./WorkerDiagnostics";
import ClusterDiagnostics from "./ClusterDiagnostics";
import InterventionRecommendations from "./InterventionRecommendations";
import WorkerResults from "./WorkerResults";
import AdminPsychometrics from "./AdminPsychometrics";
import AdminInspectionData from "./AdminInspectionData";
import AdminNetworkMetrics from "./AdminNetworkMetrics";
import AdminCompliance from "./AdminCompliance";
import { IncidentSignals } from "@/components/admin/IncidentSignals";
import { IncidentManagement } from "@/components/admin/IncidentManagement";
import { RiskPulseSidebar } from "@/components/admin/RiskPulseSidebar";
import { SafetyCopilot } from "@/components/admin/SafetyCopilot";
import AdminFatigueManagement from "./AdminFatigueManagement";
import { UpgradeGate, getTierBadgeLabel } from "@/components/admin/UpgradeGate";
import { useSubscriptionFeatures, useUserCompanyId } from "@/hooks/useSubscriptionFeatures";
import { hasTierAccess, SubscriptionTier } from "@/lib/subscriptionFeatures";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";
import { PendingUpgradeBanner } from "@/components/admin/PendingUpgradeBanner";
import { CopilotPane } from "@/components/admin/copilot/CopilotPane";
import { ChatHistoryList } from "@/components/admin/copilot/ChatHistoryList";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

// Tab to tier mapping - null means free (no restriction)
type TierRequirement = 'professional' | 'enterprise' | 'enterprise_plus' | null;

const TAB_TIERS: Record<string, TierRequirement> = {
  // COPILOT
  'copilot': 'professional',
  // SIGNALS
  'reports': null,
  'incident-signals': null,
  'checkins': null,
  'inspection-data': 'professional',
  'psychometrics': null,
  'network-metrics': 'professional',
  // DIAGNOSE
  'incident-management': null,
  'diagnostics': 'professional',
  'cluster-diagnostics': 'professional',
  'friction': 'professional',
  'fatigue': 'enterprise',
  // ACT
  'recommendations': 'enterprise',
  'intervention': 'professional',
  'supervisor-action': null,
  // RESULTS
  'worker-results': 'enterprise',
  'experiments': 'enterprise',
  'analytics': null,
  // CONFIGURE
  'users': null,
  'settings': null,
  'positions': null,
  'departments': null,
  'locations': null,
  'documents': 'professional',
  'compliance': 'professional',
};

const AdminDashboard = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast: toastHook } = useToast();

  // Get subscription data for reactive tier checks
  const { companyId, siteId: userSiteId } = useUserCompanyId();
  const { subscription, hasFeature, isLoading: subLoading } = useSubscriptionFeatures(companyId || undefined);
  const currentTier = subscription?.tier || 'free';

  const [activeTab, setActiveTab] = useState(() => {
    const tab = new URLSearchParams(window.location.search).get("tab");
    return tab || "copilot";
  });
  const [activeConversationId, setActiveConversationId] = useState<string | null>(() => {
    return new URLSearchParams(window.location.search).get("c");
  });
  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() => {
    try {
      const raw = localStorage.getItem("admin:openGroups");
      if (raw) return JSON.parse(raw);
    } catch {}
    return { SIGNALS: true, DIAGNOSE: false, ACT: false, RESULTS: false, CONFIGURE: false };
  });
  const toggleGroup = (label: string) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [label]: !prev[label] };
      try { localStorage.setItem("admin:openGroups", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalLocations: 0,
    totalReports: 0,
    pendingReports: 0
  });
  const [pendingUpgrade, setPendingUpgrade] = useState<{
    requestedTier: string;
    requestedAt: string;
  } | null>(null);

  // Keep URL (tab=...) and UI tab state in sync.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get("tab");
    if (tab && tab !== activeTab) setActiveTab(tab);
    const c = params.get("c");
    if (c !== activeConversationId) setActiveConversationId(c);
  }, [location.search]);

  const setTab = (tabId: string, conversationId?: string | null) => {
    setActiveTab(tabId);
    const params = new URLSearchParams(location.search);
    params.set("tab", tabId);
    if (conversationId !== undefined) {
      if (conversationId) params.set("c", conversationId);
      else params.delete("c");
      setActiveConversationId(conversationId);
    } else {
      // For any tab change without explicit conversationId, clear `c`
      // (prevents "New chat" from preserving the previous conversation)
      params.delete("c");
      setActiveConversationId(null);
    }
    navigate(`/admin?${params.toString()}`, { replace: true });
  };

  const openConversation = (id: string) => setTab("copilot", id);
  const newChat = () => setTab("copilot", null);

  useEffect(() => {
    checkAuth();
    fetchStats();
  }, []);

  // Fetch pending upgrade status when companyId is available
  useEffect(() => {
    const fetchPendingUpgrade = async () => {
      if (!companyId) return;
      
      const { data } = await supabase
        .from('companies')
        .select('upgrade_requested_tier, upgrade_requested_at')
        .eq('id', companyId)
        .single();
      
      if (data?.upgrade_requested_tier) {
        setPendingUpgrade({
          requestedTier: data.upgrade_requested_tier,
          requestedAt: data.upgrade_requested_at,
        });
      }
    };
    
    fetchPendingUpgrade();
  }, [companyId]);

  const checkAuth = async () => {
    const {
      data: {
        user
      }
    } = await supabase.auth.getUser();
    if (!user) {
      navigate("/admin/login");
      return;
    }

    // Check for admin OR super_admin role
    const {
      data: roleData
    } = await supabase.from("user_roles").select("role").eq("user_id", user.id).in("role", ["admin", "super_admin"]);
    if (!roleData || roleData.length === 0) {
      toastHook({
        title: "Access Denied",
        description: "You don't have admin privileges.",
        variant: "destructive"
      });
      navigate("/");
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toastHook({
      title: "Logged Out",
      description: "You have been successfully logged out."
    });
    navigate("/admin/login");
  };

  const fetchStats = async () => {
    try {
      const [usersRes, locationsRes, reportsRes, pendingRes] = await Promise.all([supabase.from("worker_profiles").select("id", {
        count: "exact",
        head: true
      }), supabase.from("company_locations").select("id", {
        count: "exact",
        head: true
      }), supabase.from("peer_reports").select("id", {
        count: "exact",
        head: true
      }), supabase.from("peer_reports").select("id", {
        count: "exact",
        head: true
      }).eq("verification_status", "pending_verification")]);
      setStats({
        totalUsers: usersRes.count || 0,
        totalLocations: locationsRes.count || 0,
        totalReports: reportsRes.count || 0,
        pendingReports: pendingRes.count || 0
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  // S-I-A-R Framework Navigation
  const navSections = [
    {
      label: "SIGNALS",
      items: [
        { id: "reports", label: "Reports", icon: FileText },
        { id: "incident-signals", label: "Incident Signals", icon: ShieldAlert },
        { id: "checkins", label: "Check-ins", icon: Clock },
        { id: "inspection-data", label: "Inspection Data", icon: ClipboardCheck },
        { id: "psychometrics", label: "Psychometrics", icon: ClipboardList },
        { id: "network-metrics", label: "Network Metrics", icon: Network },
      ]
    },
    {
      label: "DIAGNOSE",
      items: [
        { id: "incident-management", label: "Incident Management", icon: ShieldAlert },
        { id: "diagnostics", label: "Worker Diagnostics", icon: Users },
        { id: "cluster-diagnostics", label: "Cluster Diagnostics", icon: Activity },
        { id: "friction", label: "Friction Map", icon: MapPin },
        { id: "fatigue", label: "Fatigue Management", icon: AlertTriangle },
      ]
    },
    {
      label: "ACT",
      items: [
        { id: "recommendations", label: "Recommendations", icon: Zap },
        { id: "intervention", label: "Interventions", icon: Rocket },
        { id: "supervisor-action", label: "Supervisor Action", icon: CheckSquare },
      ]
    },
    {
      label: "RESULTS",
      items: [
        { id: "worker-results", label: "Worker Results", icon: Activity },
        { id: "experiments", label: "Experiments", icon: Lightbulb },
        { id: "analytics", label: "Analytics", icon: BarChart3 },
        { id: "compliance", label: "Compliance & Audit", icon: ShieldCheck },
      ]
    },
    {
      label: "CONFIGURE",
      items: [
        { id: "users", label: "Users", icon: Users },
        { id: "settings", label: "Settings", icon: Settings },
        { id: "positions", label: t("admin.positions"), icon: Briefcase },
        { id: "departments", label: t("admin.departments"), icon: Factory },
        { id: "locations", label: t("admin.locations"), icon: MapPin },
        { id: "documents", label: "Documents", icon: BookOpen },
      ]
    }
  ];

  // Helper to wrap content with UpgradeGate if needed
  const wrapWithGate = (content: React.ReactNode, tier: TierRequirement, featureName: string) => {
    if (!tier) return content;
    return (
      <UpgradeGate requiredTier={tier} featureName={featureName}>
        {content}
      </UpgradeGate>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case "copilot":
        return wrapWithGate(
          <CopilotPane
            companyId={companyId || null}
            siteId={null}
            conversationId={activeConversationId}
            onConversationCreated={(id) => setTab("copilot", id)}
          />,
          TAB_TIERS['copilot'],
          'AI Safety Copilot'
        );
        return <AdminPsychometrics />;
      case "inspection-data":
        return wrapWithGate(<AdminInspectionData />, TAB_TIERS['inspection-data'], 'Inspection Data');
      case "network-metrics":
        return wrapWithGate(<AdminNetworkMetrics />, TAB_TIERS['network-metrics'], 'Network Metrics');
      case "analytics":
        return <AdminAnalytics />;
      case "friction":
        return wrapWithGate(<AdminFrictionMapping />, TAB_TIERS['friction'], 'Friction Mapping');
      case "reports":
        return <AdminReports />;
      case "incident-signals":
        return <IncidentSignals />;
      case "incident-management":
        return <IncidentManagement />;
      case "documents":
        return wrapWithGate(<AdminDocuments />, TAB_TIERS['documents'], 'Document Management');
      case "compliance":
        return wrapWithGate(<AdminCompliance />, TAB_TIERS['compliance'], 'Compliance & Audit');
      case "users":
        return <AdminUsers />;
      case "diagnostics":
        return wrapWithGate(<WorkerDiagnostics />, TAB_TIERS['diagnostics'], 'Worker Diagnostics');
      case "cluster-diagnostics":
        return wrapWithGate(<ClusterDiagnostics />, TAB_TIERS['cluster-diagnostics'], 'Cluster Diagnostics');
      case "recommendations":
        return wrapWithGate(<InterventionRecommendations />, TAB_TIERS['recommendations'], 'AI Recommendations');
      case "checkins":
        return <AdminCheckins />;
      case "positions":
        return <AdminPositions />;
      case "departments":
        return <AdminDepartments />;
      case "locations":
        return <AdminSites />;
      case "behavioral":
        return <AdminBehavioral />;
      case "intervention":
        return wrapWithGate(<AdminIntervention />, TAB_TIERS['intervention'], 'Interventions');
      case "supervisor-action":
        return <SupervisorAction />;
      case "results":
        return <ResultsDashboard />;
      case "worker-results":
        return wrapWithGate(<WorkerResults />, TAB_TIERS['worker-results'], 'Worker Results');
      case "experiments":
        return wrapWithGate(<AdminExperimentResults />, TAB_TIERS['experiments'], 'A/B Experiments');
      case "fatigue":
        return wrapWithGate(<AdminFatigueManagement />, TAB_TIERS['fatigue'], 'Fatigue Management');
      case "settings":
        return <AdminSettings />;
      default:
        return <AdminReports />;
    }
  };

  const StatCard = ({
    title,
    value,
    icon: Icon,
    trend,
    variant = "default"
  }: {
    title: string;
    value: number;
    icon: any;
    trend?: string;
    variant?: "default" | "warning" | "success";
  }) => <Card className={cn("border-0 shadow-sm hover:shadow-md transition-shadow", variant === "warning" && "bg-warning/5 border-l-4 border-l-warning", variant === "success" && "bg-success/5 border-l-4 border-l-success")}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm text-muted-foreground mb-1">{title}</p>
            <p className="text-3xl font-bold">{value}</p>
            {trend && <p className="text-xs text-success flex items-center gap-1 mt-2">
                <TrendingUp className="h-3 w-3" />
                {trend}
              </p>}
          </div>
          <div className={cn("p-3 rounded-2xl", variant === "warning" ? "bg-warning/10" : variant === "success" ? "bg-success/10" : "bg-primary/10")}>
            <Icon className={cn("h-6 w-6", variant === "warning" ? "text-warning" : variant === "success" ? "text-success" : "text-primary")} />
          </div>
        </div>
      </CardContent>
    </Card>;

  // Render nav item with lock indicator (reactive to subscription tier)
  const renderNavItem = (item: { id: string; label: string; icon: any }, isMobile = false) => {
    const requiredTier = TAB_TIERS[item.id];
    // Only show lock if there's a required tier AND current tier doesn't meet it
    const isLocked = requiredTier !== null && !hasTierAccess(currentTier, requiredTier);

    return (
      <button 
        key={item.id} 
        onClick={() => { 
          setTab(item.id); 
          if (isMobile) setMobileSidebarOpen(false);
        }} 
        className={cn(
          "w-full flex items-center gap-3 px-4 py-2.5 rounded-xl transition-colors text-left",
          activeTab === item.id 
            ? "bg-primary text-primary-foreground" 
            : "hover:bg-secondary text-muted-foreground hover:text-foreground"
        )}
      >
        <item.icon className="h-4 w-4 shrink-0" />
        {(sidebarOpen || isMobile) && (
          <>
            <span className="text-sm font-medium flex-1">{item.label}</span>
            {isLocked && requiredTier && (
              <div className="flex items-center gap-1">
                <Lock className={cn(
                  "h-3 w-3",
                  activeTab === item.id ? "text-primary-foreground/70" : "text-muted-foreground"
                )} />
                <Badge 
                  variant="outline" 
                  className={cn(
                    "text-[9px] px-1 py-0 h-4",
                    activeTab === item.id 
                      ? "border-primary-foreground/30 text-primary-foreground/70" 
                      : "border-muted-foreground/30"
                  )}
                >
                  {getTierBadgeLabel(requiredTier)}
                </Badge>
              </div>
            )}
          </>
        )}
      </button>
    );
  };

  return <>
      {/* Pending Upgrade Banner - shows on first login after paid tier signup */}
      {pendingUpgrade && (
        <PendingUpgradeBanner 
          requestedTier={pendingUpgrade.requestedTier}
          requestedAt={pendingUpgrade.requestedAt}
          onDismiss={() => setPendingUpgrade(null)}
        />
      )}
      
      <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className={cn("hidden lg:flex flex-col bg-card border-r transition-all duration-300", sidebarOpen ? "w-64" : "w-20")}>
        {/* Logo */}
        <div className="p-6 border-b">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-success flex items-center justify-center shrink-0">
              <img src="/src/assets/logo-symbol-white.svg" alt="SORIN" className="h-5 w-5" />
            </div>
            {sidebarOpen && <div>
                <h1 className="font-bold text-lg">SORIN</h1>
                <p className="text-xs text-muted-foreground">Admin Panel</p>
              </div>}
          </div>
        </div>

        {/* Nav Items */}
        <nav className="flex-1 p-4 space-y-4 overflow-y-auto">
          {/* AI COPILOT group */}
          {sidebarOpen && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground mb-2 px-2 tracking-wider flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> AI COPILOT
              </p>
              {renderNavItem({ id: "copilot", label: "New chat", icon: Plus })}
              <div className="mt-2">
                <ChatHistoryList
                  activeId={activeTab === "copilot" ? activeConversationId : null}
                  onSelect={openConversation}
                  locked={TAB_TIERS['copilot'] !== null && !hasTierAccess(currentTier, TAB_TIERS['copilot']!)}
                  requiredTier={TAB_TIERS['copilot']}
                />
              </div>
            </div>
          )}
          {!sidebarOpen && renderNavItem({ id: "copilot", label: "Copilot", icon: Sparkles })}

          {/* SIAR + CONFIGURE collapsible groups */}
          {navSections.map(section => {
            const isOpen = openGroups[section.label] ?? false;
            if (!sidebarOpen) {
              return (
                <div key={section.label} className="space-y-1">
                  {section.items.map(item => renderNavItem(item))}
                </div>
              );
            }
            return (
              <Collapsible key={section.label} open={isOpen} onOpenChange={() => toggleGroup(section.label)}>
                <CollapsibleTrigger className="w-full flex items-center justify-between px-2 mb-1 group">
                  <span className="text-[10px] font-semibold text-muted-foreground tracking-wider">
                    {section.label}
                  </span>
                  <ChevronDown className={cn("h-3 w-3 text-muted-foreground transition-transform", !isOpen && "-rotate-90")} />
                </CollapsibleTrigger>
                <CollapsibleContent className="space-y-1">
                  {section.items.map(item => renderNavItem(item))}
                </CollapsibleContent>
              </Collapsible>
            );
          })}
        </nav>

        {/* Risk Pulse Widget in Sidebar */}
        <div className="p-4 border-t">
          <RiskPulseSidebar collapsed={!sidebarOpen} />
        </div>

        {/* Toggle & Logout */}
        <div className="p-4 border-t space-y-2">
          <Button variant="ghost" className="w-full justify-start gap-3" onClick={() => setSidebarOpen(!sidebarOpen)}>
            <ChevronRight className={cn("h-5 w-5 transition-transform", !sidebarOpen && "rotate-180")} />
            {sidebarOpen && <span>Collapse</span>}
          </Button>
          <Button variant="ghost" className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={handleLogout}>
            <LogOut className="h-5 w-5" />
            {sidebarOpen && <span>{t("admin.logout")}</span>}
          </Button>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <AnimatePresence>
        {mobileSidebarOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-foreground/20 backdrop-blur-sm z-50 lg:hidden" onClick={() => setMobileSidebarOpen(false)} />
            <motion.aside initial={{
          x: "-100%"
        }} animate={{
          x: 0
        }} exit={{
          x: "-100%"
        }} transition={{
          type: "spring",
          damping: 25,
          stiffness: 200
        }} className="fixed left-0 top-0 bottom-0 w-72 bg-card z-50 lg:hidden shadow-2xl">
              <div className="p-4 border-b flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                    <span className="text-primary-foreground font-bold">S</span>
                  </div>
                  <div>
                    <h1 className="font-bold">SORIN Admin</h1>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setMobileSidebarOpen(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <nav className="p-4 space-y-4 overflow-y-auto max-h-[calc(100vh-180px)]">
                <div>
                  <p className="text-[10px] font-semibold text-muted-foreground mb-2 px-2 tracking-wider flex items-center gap-1">
                    <Sparkles className="h-3 w-3" /> AI COPILOT
                  </p>
                  {renderNavItem({ id: "copilot", label: "New chat", icon: Plus }, true)}
                  <div className="mt-2">
                    <ChatHistoryList
                      activeId={activeTab === "copilot" ? activeConversationId : null}
                      onSelect={(id) => { openConversation(id); setMobileSidebarOpen(false); }}
                      locked={TAB_TIERS['copilot'] !== null && !hasTierAccess(currentTier, TAB_TIERS['copilot']!)}
                      requiredTier={TAB_TIERS['copilot']}
                    />
                  </div>
                </div>
                {navSections.map(section => {
                  const isOpen = openGroups[section.label] ?? false;
                  return (
                    <Collapsible key={section.label} open={isOpen} onOpenChange={() => toggleGroup(section.label)}>
                      <CollapsibleTrigger className="w-full flex items-center justify-between px-2 mb-1">
                        <span className="text-[10px] font-semibold text-muted-foreground tracking-wider">{section.label}</span>
                        <ChevronDown className={cn("h-3 w-3 text-muted-foreground transition-transform", !isOpen && "-rotate-90")} />
                      </CollapsibleTrigger>
                      <CollapsibleContent className="space-y-1">
                        {section.items.map(item => renderNavItem(item, true))}
                      </CollapsibleContent>
                    </Collapsible>
                  );
                })}
              </nav>
              <div className="p-4 border-t">
                <Button variant="ghost" className="w-full justify-start gap-3 text-destructive hover:text-destructive hover:bg-destructive/10" onClick={handleLogout}>
                  <LogOut className="h-5 w-5" />
                  <span>{t("admin.logout")}</span>
                </Button>
              </div>
            </motion.aside>
          </>}
      </AnimatePresence>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen overflow-hidden">
        {/* Top Header */}
        <header className="sticky top-0 z-30 pt-safe bg-background/80 backdrop-blur-xl border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setMobileSidebarOpen(true)}>
                <Menu className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold">{t("admin.title")}</h1>
                <p className="text-sm text-muted-foreground">{t("admin.manageSystem")}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <LanguageSwitcher />
              <Button variant="outline" onClick={handleLogout} className="hidden sm:flex">
                <LogOut className="h-4 w-4 mr-2" />
                {t("admin.logout")}
              </Button>
            </div>
          </div>
        </header>

        {/* Dashboard Content - Full Width */}
        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {renderContent()}
          </div>
        </main>

        {/* AI Copilot FAB - Enterprise+ only */}
        {hasFeature('aiCopilot') ? (
          <Button
            onClick={() => setCopilotOpen(true)}
            className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg z-40"
            size="icon"
          >
            <Bot className="h-6 w-6" />
          </Button>
        ) : (
          <Button
            onClick={() => toast.error('AI Copilot requires Enterprise plan or higher', {
              action: { label: 'Upgrade', onClick: () => navigate('/admin?tab=settings') }
            })}
            variant="outline"
            className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg z-40 bg-muted"
            size="icon"
          >
            <Lock className="h-5 w-5 text-muted-foreground" />
          </Button>
        )}

        {/* AI Safety Copilot */}
        <SafetyCopilot isOpen={copilotOpen} onClose={() => setCopilotOpen(false)} companyId={companyId || undefined} />
      </div>
    </div>
  </>;
};
export default AdminDashboard;
